// src/services/api.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.eliteriders.co/api'; // Update the default URL

// Create an Axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Set the token as a default header for all requests
export const setAuthToken = (token) => {
    if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        localStorage.setItem('token', token); // Store the token in local storage
    } else {
        delete api.defaults.headers.common['Authorization'];
        localStorage.removeItem('token'); // Remove the token from local storage
    }
};

// Retrieve the token from local storage and set it as a default header
const token = localStorage.getItem('token');
if (token) {
  setAuthToken(token);
}

// Intercept all requests to add the token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    const isAuthRequest =
      requestUrl.includes('/users/login')
      || requestUrl.includes('/login/send-otp')
      || requestUrl.includes('/login/verify-otp');

    if (status === 401 && !isAuthRequest && localStorage.getItem('token')) {
      const { forceLogout } = await import('../utils/sessionAuth');
      await forceLogout('expired');
    }

    return Promise.reject(error);
  }
);

  export const loginUser = (username, password) => {
    return api.post('/users/login', { username, password });
  };

  export const sendLoginOtp = (phone) => {
    return api.post('/users/login/send-otp', { phone });
  };

  export const verifyLoginOtp = (phone, otp) => {
    return api.post('/users/login/verify-otp', { phone, otp });
  };
  
  export const createOrder = (orderData) => {
    return api.post('/orders', orderData);
  };
  
  export const getOrders = (params = {}) => {
    return api.get('/orders', { params });
  };
  
  export const updateOrder = (orderId, orderData) => {
    return api.put(`/orders/${orderId}`, orderData);
  };

  export const updateOrderStatus = (orderId, statusName) => {
    return api.put(`/orders/${orderId}/status`, { status_name: statusName });
  };

  export const manualDeliveryOverride = (orderId, receiverName) => {
    return api.post(`/orders/${orderId}/manual-delivery-override`, { receiverName });
  };

  export const verifyOtp = async (orderId, otp, receiverName) => {
    try {
      const response = await api.post(`/orders/${orderId}/verify-otp`, { otp, receiverName });
      return response;
    } catch (error) {
      throw error;
    }
  };

export const getUsers = () => {
  return api.get('/users');
};

export const createUser = (userData) => {
  return api.post('/users/register', userData);
};

export const updateUser = (userId, userData) => {
  return api.put(`/users/${userId}`, userData);
};

export const deleteUser = (userId) => {
  return api.delete(`/users/${userId}`);
};

export const updateUserStatus = (userId, isActive) => {
  return api.patch(`/users/${userId}/status`, { is_active: isActive });
};

export const getOrderHistory = (orderId) => {
  return api.get(`/orders/${orderId}/history`);
};

export const getOrderSmsStatus = (orderId) => {
  return api.get(`/orders/${orderId}/sms-status`);
};

export const getOrderSmsStatusesBatch = (orderIds = []) => {
  const uniqueIds = [...new Set(orderIds.filter(Boolean))];
  if (uniqueIds.length === 0) {
    return Promise.resolve({ data: {} });
  }
  return api.get(`/orders/sms-status/batch?ids=${uniqueIds.join(',')}`);
};

export const updatePassword = (passwordData) => {
  return api.put('/users/password', passwordData);
};

export const getAnalytics = (range, companyId, period) => {
  let url = `/orders/analytics/dashboard?range=${range}`;
  if (companyId) {
    url += `&company_id=${companyId}`;
  }
  if (period?.year && period?.month) {
    url += `&year=${period.year}&month=${period.month}`;
  }
  return api.get(url);
};

export const getCurrentUser = () => {
  return api.get('/users/me');
};

export const updateUserDetails = (userData) => {
  return api.put('/users/me', userData);
};

// Function to fetch order history
export const fetchOrderHistory = (orderId) => {
    const token = localStorage.getItem('token');
    return api.get(`/orders/${orderId}/history`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
};

// Add the checkUserDeletable function
export const checkUserDeletable = (userId) => {
    return api.get(`/users/${userId}/deletable`);
};

export const getCompanies = () => {
  return api.get('/companies');
};

export const createCompany = async (companyData) => {
    const response = await api.post('/companies', companyData);
    return response.data;
};

export const updateCompany = async (id, companyData) => {
    const response = await api.put(`/companies/${id}`, companyData);
    return response.data;
};

export const adminResetUserPassword = async (userId, newPassword) => {
  const response = await api.post(`/users/${userId}/admin-reset-password`, { newPassword });
  return response.data;
};

// New function to resend OTP
export const resendOtp = async (orderId) => {
  try {
    const response = await api.post(`/orders/${orderId}/resend-otp`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const resendOrderReceivedSms = async (orderId) => {
  try {
    const response = await api.post(`/orders/${orderId}/resend-order-received-sms`);
    return response;
  } catch (error) {
    throw error;
  }
};

// Customer API functions
export const getCustomers = (params = {}) => {
  return api.get('/customers', { params });
};

export const getCustomerStatistics = (params = {}) => {
  return api.get('/customers/statistics', { params });
};

export const getCustomer = (customerId) => {
  return api.get(`/customers/${customerId}`);
};

export const createCustomer = (customerData) => {
  return api.post('/customers', customerData);
};

export const updateCustomer = (customerId, customerData) => {
  return api.put(`/customers/${customerId}`, customerData);
};

export const updateCustomerStatus = (customerId, isActive) => {
  return api.patch(`/customers/${customerId}/status`, { is_active: isActive });
};

export default api;